<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card p-fluid followup-settings">
        <h4>Follow-up Emails</h4>
        <p class="hint">
          How and when quotes are chased by email. Changes apply to the next send.
        </p>

        <div v-if="loading" class="p-text-center"><ProgressSpinner /></div>

        <div v-else-if="loadError" class="p-invalid">{{ loadError }}</div>

        <div v-else>
          <div id="followupSummary" class="followup-summary p-mb-3">{{ summary }}</div>

          <TabView>
            <TabPanel :header="tabHeader('sending')" :active.sync="tabActive.sending">
              <p class="hint tab-explain">{{ tabExplain('sending') }}</p>
              <div class="p-field p-d-flex p-ai-center">
                <InputSwitch id="enabled" v-model="form.enabled" />
                <label for="enabled" class="p-ml-2 p-mb-0">Send follow-up emails</label>
              </div>
              <small class="hint">When off, nothing is sent and no quote is enrolled. When switched on, an email whose date has already passed is skipped: only emails due from then on go out.</small>
              <div v-if="serverWarning" id="serverSwitchWarning" class="server-warning p-mt-2">{{ serverWarning }}</div>
              <small id="switchedOnAt" class="hint p-d-block" v-if="saved && saved.enabled && saved.switchedOnAt">Switched on {{ saved.switchedOnAt }}.</small>
              <small class="p-invalid" v-if="errors.enabled">{{ errors.enabled }}</small>

              <div class="p-formgrid p-grid">
                <div class="p-field p-col">
                  <label for="workStart">Working day starts</label>
                  <InputMask id="workStart" v-model="form.workStart" mask="99:99" placeholder="09:00"
                    :class="{ 'p-invalid': errors.workStart }" />
                  <small class="p-invalid" v-if="errors.workStart">{{ errors.workStart }}</small>
                </div>
                <div class="p-field p-col">
                  <label for="workEnd">Working day ends</label>
                  <InputMask id="workEnd" v-model="form.workEnd" mask="99:99" placeholder="17:00"
                    :class="{ 'p-invalid': errors.workEnd }" />
                  <small class="p-invalid" v-if="errors.workEnd">{{ errors.workEnd }}</small>
                </div>
              </div>
              <small class="hint">UK time. An email due outside these hours waits for the next working day.</small>

              <div class="p-field">
                <label>Working days</label>
                <div class="p-d-flex p-flex-wrap">
                  <div v-for="d in weekDays" :key="d.value" class="p-field-checkbox p-mr-3 p-mb-0">
                    <Checkbox :id="'workDay' + d.value" name="workDays" :value="d.value" v-model="form.workDays" />
                    <label :for="'workDay' + d.value">{{ d.label }}</label>
                  </div>
                </div>
                <small class="hint">Bank holidays are skipped as well.</small>
                <small class="p-invalid" v-if="errors.workDays">{{ errors.workDays }}</small>
              </div>

              <div class="p-field">
                <label for="dailyCap">Daily limit</label>
                <InputNumber id="dailyCap" v-model="form.dailyCap" :min="0" :useGrouping="false"
                  :class="{ 'p-invalid': errors.dailyCap }" />
                <small class="hint">The most follow-up emails sent in one day. The rest go the next working day.</small>
                <small class="p-invalid" v-if="errors.dailyCap">{{ errors.dailyCap }}</small>
              </div>

              <h5>Email addresses</h5>
              <div class="p-field">
                <label for="internalRecipientOverride">Send all follow-up emails to (testing only)</label>
                <InputText id="internalRecipientOverride" v-model="form.internalRecipientOverride"
                  :class="{ 'p-invalid': errors.internalRecipientOverride }" />
                <small class="hint">One email address. When set, no customer receives anything; every email goes here. Leave blank in normal use.</small>
                <small class="p-invalid" v-if="errors.internalRecipientOverride">{{ errors.internalRecipientOverride }}</small>
              </div>

              <div class="p-field">
                <label for="tenantDomains">Company email domains for Outlook</label>
                <InputText id="tenantDomains" v-model="form.tenantDomains" placeholder="e.g. mbp.co.uk"
                  :class="{ 'p-invalid': errors.tenantDomains }" />
                <small class="hint">Separate with commas. Leave blank for no check.</small>
                <small class="p-invalid" v-if="errors.tenantDomains">{{ errors.tenantDomains }}</small>
              </div>
            </TabPanel>
            <TabPanel :header="tabHeader('chased')" :active.sync="tabActive.chased">
              <p class="hint tab-explain">{{ tabExplain('chased') }}</p>
              <div class="p-field">
                <label for="eligibleQuoteStatuses">Chase quotes with these statuses</label>
                <MultiSelect id="eligibleQuoteStatuses" v-model="form.eligibleQuoteStatuses" :options="quoteStatuses"
                  optionLabel="statusName" optionValue="statusName" placeholder="Choose"
                  :class="{ 'p-invalid': errors.eligibleQuoteStatuses }" />
                <small class="hint">A quote that moves to any other status stops being chased.</small>
                <small class="p-invalid" v-if="errors.eligibleQuoteStatuses">{{ errors.eligibleQuoteStatuses }}</small>
              </div>

              <div class="p-field-checkbox">
                <Checkbox id="skipExpiredQuotes" v-model="form.skipExpiredQuotes" :binary="true" />
                <label for="skipExpiredQuotes">Don't chase expired quotes</label>
              </div>
              <small class="hint">Quotes past their expiry date are left alone.</small>
              <small class="p-invalid" v-if="errors.skipExpiredQuotes">{{ errors.skipExpiredQuotes }}</small>
            </TabPanel>
            <TabPanel :header="tabHeader('types')" :active.sync="tabActive.types">
              <p class="hint tab-explain">{{ tabExplain('types') }}</p>
              <FollowupTypes v-if="tabActive.types" :defaultSettings="saved"
                :defaultStepDays="defaultStepDays" />
            </TabPanel>
            <TabPanel :header="tabHeader('approval')" :active.sync="tabActive.approval">
              <p class="hint tab-explain">{{ tabExplain('approval') }}</p>
              <div class="p-field-checkbox">
                <Checkbox id="firstSendNeedsApproval" v-model="form.firstSendNeedsApproval" :binary="true" />
                <label for="firstSendNeedsApproval">First email needs approval</label>
              </div>
              <small class="hint">The salesperson approves each quote on its Follow-up tab before the first email goes.</small>
              <small class="p-invalid" v-if="errors.firstSendNeedsApproval">{{ errors.firstSendNeedsApproval }}</small>

              <div class="p-field-checkbox p-mt-2">
                <Checkbox id="manualEnrolmentNeedsApproval" v-model="form.manualEnrolmentNeedsApproval" :binary="true" />
                <label for="manualEnrolmentNeedsApproval">Hand-enrolled quotes need approval</label>
              </div>
              <small class="hint">Applies to quotes enrolled from the Enrol Quotes screen or the Follow-up tab.</small>
              <small class="p-invalid" v-if="errors.manualEnrolmentNeedsApproval">{{ errors.manualEnrolmentNeedsApproval }}</small>

              <div class="p-field-checkbox p-mt-3">
                <Checkbox id="approvalEmailEnabled" v-model="form.approvalEmailEnabled" :binary="true" />
                <label for="approvalEmailEnabled">Email the salesperson to approve the first email</label>
              </div>
              <small class="hint">The working day before it is due: "Approve Follow-up Email for Quote: ..." with the text and Approve / Edit / Suspend. Unanswered, the email does not go.</small>
              <small class="p-invalid" v-if="errors.approvalEmailEnabled">{{ errors.approvalEmailEnabled }}</small>
              <div class="p-formgrid p-grid p-mt-2">
                <div class="p-field p-col-12 p-md-8">
                  <label for="approvalEscalationUserIds">If the salesperson does not answer, escalate to</label>
                  <MultiSelect id="approvalEscalationUserIds" v-model="form.approvalEscalationUserIds" :options="activeUsers"
                    optionLabel="name" optionValue="id" :filter="true" placeholder="Nobody"
                    :class="{ 'p-invalid': errors.approvalEscalationUserIds }" />
                  <small class="hint">They get the unanswered approval email with the quote and the link, and may answer it themselves. The customer email still waits for an answer.</small>
                  <small class="p-invalid" v-if="errors.approvalEscalationUserIds">{{ errors.approvalEscalationUserIds }}</small>
                </div>
                <div class="p-field p-col-12 p-md-4">
                  <label for="approvalEscalationWorkingDays">after (working days)</label>
                  <InputNumber id="approvalEscalationWorkingDays" v-model="form.approvalEscalationWorkingDays" :min="1" :useGrouping="false"
                    :class="{ 'p-invalid': errors.approvalEscalationWorkingDays }" />
                  <small class="p-invalid" v-if="errors.approvalEscalationWorkingDays">{{ errors.approvalEscalationWorkingDays }}</small>
                </div>
              </div>
              <div class="p-field p-mt-2">
                <label for="suspendReasons">Reasons offered when suspending (one per line)</label>
                <Textarea id="suspendReasons" v-model="form.suspendReasons" :autoResize="true" rows="4"
                  :class="{ 'p-invalid': errors.suspendReasons }" />
                <small class="hint">"Other" (with the salesperson's own words) is always offered too. Answers are listed under Follow-up Settings › Approval Feedback.</small>
                <small class="p-invalid" v-if="errors.suspendReasons">{{ errors.suspendReasons }}</small>
              </div>
            </TabPanel>
            <TabPanel :header="tabHeader('escalation')" :active.sync="tabActive.escalation">
              <p class="hint tab-explain">{{ tabExplain('escalation') }}</p>
              <div class="p-formgrid p-grid">
                <div class="p-field p-col">
                  <label for="silenceEscalationDays">Days of silence before escalating</label>
                  <InputNumber id="silenceEscalationDays" v-model="form.silenceEscalationDays" :min="1" :useGrouping="false"
                    :class="{ 'p-invalid': errors.silenceEscalationDays }" />
                  <small class="hint">Counted from the last step sent.</small>
                  <small class="p-invalid" v-if="errors.silenceEscalationDays">{{ errors.silenceEscalationDays }}</small>
                </div>
                <div class="p-field p-col">
                  <label for="escalationRecipient">Escalation goes to</label>
                  <Dropdown id="escalationRecipient" v-model="form.escalationRecipient" :options="escalationRecipients"
                    optionLabel="label" optionValue="value" placeholder="Choose"
                    :class="{ 'p-invalid': errors.escalationRecipient }" />
                  <small class="p-invalid" v-if="errors.escalationRecipient">{{ errors.escalationRecipient }}</small>
                </div>
              </div>
              <div class="p-formgrid p-grid">
                <div class="p-field p-col">
                  <label for="onLoggedContact">When a call, meeting or note is logged on the quote</label>
                  <Dropdown id="onLoggedContact" v-model="form.onLoggedContact" :options="contactModes"
                    optionLabel="label" optionValue="value" :class="{ 'p-invalid': errors.onLoggedContact }" />
                  <small class="hint">A completed call or meeting, or a note, logged since the last automatic email.</small>
                  <small class="p-invalid" v-if="errors.onLoggedContact">{{ errors.onLoggedContact }}</small>
                </div>
                <div class="p-field p-col" v-if="form.onLoggedContact === 'PAUSE'">
                  <label for="pauseDaysAfterContact">Pause the next email for (days after the contact)</label>
                  <InputNumber id="pauseDaysAfterContact" v-model="form.pauseDaysAfterContact" :min="1" :useGrouping="false"
                    :class="{ 'p-invalid': errors.pauseDaysAfterContact }" />
                  <small class="p-invalid" v-if="errors.pauseDaysAfterContact">{{ errors.pauseDaysAfterContact }}</small>
                </div>
              </div>
              <div class="p-formgrid p-grid">
                <div class="p-field p-col">
                  <label for="warnDaysAhead">Warn when saving a task or event about an email due within (days)</label>
                  <InputNumber id="warnDaysAhead" v-model="form.warnDaysAhead" :min="0" :useGrouping="false"
                    :class="{ 'p-invalid': errors.warnDaysAhead }" />
                  <small class="hint">Counted from the task or event's date. The warning offers Keep / Skip this one / Stop all for this quote.</small>
                  <small class="p-invalid" v-if="errors.warnDaysAhead">{{ errors.warnDaysAhead }}</small>
                </div>
                <div class="p-field p-col">
                  <label for="outboxWorkingDays">"Emails Going Out" shows (working days)</label>
                  <InputNumber id="outboxWorkingDays" v-model="form.outboxWorkingDays" :min="1" :useGrouping="false"
                    :class="{ 'p-invalid': errors.outboxWorkingDays }" />
                  <small class="hint">Today included. Each user sees their own emails; Admin sees all.</small>
                  <small class="p-invalid" v-if="errors.outboxWorkingDays">{{ errors.outboxWorkingDays }}</small>
                </div>
              </div>
              <div class="p-field">
                <label for="noContactReminderWorkingDays">Remind the salesperson if nothing is logged within (working days of an email)</label>
                <InputNumber id="noContactReminderWorkingDays" v-model="form.noContactReminderWorkingDays" :min="0" :useGrouping="false"
                  :class="{ 'p-invalid': errors.noContactReminderWorkingDays }" />
                <small class="hint">After each automatic email, if no completed call or meeting and no note is logged on the quote in this many working days, the salesperson gets a Call task with one reminder email. 0 = off. This replaces the old automatic chase call (see Quote types).</small>
                <small class="p-invalid" v-if="errors.noContactReminderWorkingDays">{{ errors.noContactReminderWorkingDays }}</small>
              </div>
              <div class="p-formgrid p-grid">
                <div class="p-field p-col">
                  <label for="chaseTaskDueWorkingDays">Chase task due after (working days)</label>
                  <InputNumber id="chaseTaskDueWorkingDays" v-model="form.chaseTaskDueWorkingDays" :min="1" :useGrouping="false"
                    :class="{ 'p-invalid': errors.chaseTaskDueWorkingDays }" />
                  <small class="hint">At no reply the salesperson always gets a chase Call task, with one reminder email at once. It is due this many working days later, at the start of the working day.</small>
                  <small class="p-invalid" v-if="errors.chaseTaskDueWorkingDays">{{ errors.chaseTaskDueWorkingDays }}</small>
                </div>
              </div>
              <div class="p-field-checkbox">
                <Checkbox id="escalationEmailsSalesperson" v-model="form.escalationEmailsSalesperson" :binary="true" />
                <label for="escalationEmailsSalesperson">Also email the salesperson "No reply on quote"</label>
              </div>
              <small class="hint">Off (recommended): the chase task and its reminder are the salesperson's only alert, so they get one email, not two.</small>
              <small class="p-invalid" v-if="errors.escalationEmailsSalesperson">{{ errors.escalationEmailsSalesperson }}</small>
              <template v-if="emailsSalesperson">
              <div class="p-field">
                <label for="escalationSubjectTemplate">Escalation subject to the salesperson</label>
                <InputText id="escalationSubjectTemplate" v-model="form.escalationSubjectTemplate"
                  :class="{ 'p-invalid': errors.escalationSubjectTemplate }" />
                <small class="p-invalid" v-if="errors.escalationSubjectTemplate">{{ errors.escalationSubjectTemplate }}</small>
              </div>
              <div class="p-field">
                <label for="escalationBodyTemplate">Escalation message to the salesperson</label>
                <Textarea id="escalationBodyTemplate" v-model="form.escalationBodyTemplate" :autoResize="true" rows="5"
                  :class="{ 'p-invalid': errors.escalationBodyTemplate }" />
                <small class="hint">You can use {{ mergeFieldsHint }}.</small>
                <small class="p-invalid" v-if="errors.escalationBodyTemplate">{{ errors.escalationBodyTemplate }}</small>
              </div>
              </template>
              <template v-if="escalatesToCustomer">
                <div class="p-field">
                  <label for="customerEscalationSubjectTemplate">Escalation subject to the customer</label>
                  <InputText id="customerEscalationSubjectTemplate" v-model="form.customerEscalationSubjectTemplate"
                    :class="{ 'p-invalid': errors.customerEscalationSubjectTemplate }" />
                  <small class="p-invalid" v-if="errors.customerEscalationSubjectTemplate">{{ errors.customerEscalationSubjectTemplate }}</small>
                </div>
                <div class="p-field">
                  <label for="customerEscalationBodyTemplate">Escalation message to the customer</label>
                  <Textarea id="customerEscalationBodyTemplate" v-model="form.customerEscalationBodyTemplate" :autoResize="true" rows="5"
                    :class="{ 'p-invalid': errors.customerEscalationBodyTemplate }" />
                  <small class="hint">You can use {{ mergeFieldsHint }}.</small>
                  <small class="p-invalid" v-if="errors.customerEscalationBodyTemplate">{{ errors.customerEscalationBodyTemplate }}</small>
                </div>
              </template>
            </TabPanel>
            <TabPanel :header="tabHeader('wording')" :active.sync="tabActive.wording">
              <p class="hint tab-explain">{{ tabExplain('wording') }}</p>
              <FollowupSteps :embedded="true" />
            </TabPanel>
            <TabPanel :header="tabHeader('holidays')" :active.sync="tabActive.holidays">
              <p class="hint tab-explain">{{ tabExplain('holidays') }}</p>
              <BankHolidays :embedded="true" />
            </TabPanel>
          </TabView>

          <div v-if="settingsTabShown" class="p-d-flex p-jc-end p-mt-3">
            <Button id="runFollowupNow" label="Run now" icon="pi pi-play" class="p-button-secondary p-mr-auto"
              style="width: auto" :disabled="running" @click="askRunNow" />
            <Button label="Reset" icon="pi pi-refresh" class="p-button-text p-mr-2" style="width: auto" @click="reset" />
            <Button id="saveFollowupSettings" label="Save" icon="pi pi-check" style="width: auto"
              :disabled="saving" @click="save" />
          </div>
        </div>

        </div>

        <Dialog :visible.sync="runNowDialog" header="Run follow-ups now" :modal="true" :style="{ width: '420px' }">
          <p>Run one follow-up pass now? Emails go only where the send setting allows.</p>
          <template #footer>
            <Button label="No" icon="pi pi-times" class="p-button-text" @click="runNowDialog = false" />
            <Button id="confirmRunNow" label="Yes, run now" icon="pi pi-check" class="p-button-text"
              :disabled="running" @click="runNow" />
          </template>
        </Dialog>
      </div>
    </div>
  </div>
</template>

<script>
import FollowupService, { errorMessage, fieldErrors } from '@/service/FollowupService'
import QuotationService from '@/service/QuotationService'
import UserService from '@/service/UserService'
import {
  toForm, toPayload, runNowSummary, settingsSummary, tabWithError, SETTINGS_TABS, WEEK_DAYS, ESCALATION_RECIPIENTS, CONTACT_MODES,
  serverSwitchWarning
} from './followupSettingsForm'
import FollowupSteps from './followupSteps/followupSteps.vue'
import BankHolidays from './bankHolidays/bankHolidays.vue'
import FollowupTypes from './followupTypes/FollowupTypes.vue'

function tabsActive(key) {
  return SETTINGS_TABS.reduce((out, t) => { out[t.key] = t.key === key; return out }, {})
}

export default {
  components: { FollowupSteps, BankHolidays, FollowupTypes },
  data: () => ({
    followupService: new FollowupService(),
    quotationService: new QuotationService(),
    saved: null,
    form: toForm(null),
    errors: {},
    quoteStatuses: [],
    loading: true,
    loadError: '',
    saving: false,
    runNowDialog: false,
    running: false,
    weekDays: WEEK_DAYS,
    escalationRecipients: ESCALATION_RECIPIENTS,
    contactModes: CONTACT_MODES,
    activeUsers: [],
    steps: [],
    tabActive: tabsActive('sending'),
    mergeFieldsHint: '{{contactFirstName}}, {{quoteRef}}, {{product}}, {{project}}, {{company}}, {{salespersonName}} and {{signature}}'
  }),
  computed: {
    summary() {
      return settingsSummary(this.saved, this.steps)
    },
    // Save / Reset / Run now belong to the settings tabs; the steps and bank holidays tabs save themselves
    serverWarning() {
      return serverSwitchWarning(this.saved)
    },
    settingsTabShown() {
      return !this.tabActive.wording && !this.tabActive.holidays && !this.tabActive.types
    },
    /** The active default steps' days, for the Quote types tab's "Use the default steps (7, 21 days)". */
    defaultStepDays() {
      return (this.steps || []).filter(s => s && s.active).sort((a, b) => a.stepNo - b.stepNo).map(s => s.daysAfterEnrolment)
    },
    // the customer templates are used only when the escalation goes to the customer
    /** The salesperson's "No reply" email goes only when ticked, and only to SALESPERSON or BOTH (option C). */
    emailsSalesperson() {
      return !!this.form.escalationEmailsSalesperson && this.form.escalationRecipient !== 'CUSTOMER'
    },
    escalatesToCustomer() {
      return this.form.escalationRecipient === 'CUSTOMER' || this.form.escalationRecipient === 'BOTH'
    }
  },
  created() {
    this.load()
  },
  methods: {
    tabHeader(key) {
      var t = SETTINGS_TABS.find(x => x.key === key)
      var keys = Object.keys(this.errors || {})
      return t.header + (t.fields.some(f => keys.indexOf(f) > -1) ? ' (!)' : '')
    },
    tabExplain(key) {
      return SETTINGS_TABS.find(x => x.key === key).explain
    },
    showTab(key) {
      this.tabActive = tabsActive(key)
    },
    loadSteps() {
      return this.followupService.getSteps()
        .then(data => { this.steps = data || [] })
        .catch(() => { this.steps = [] })
    },
    load() {
      this.loading = true
      new UserService().getAllActiveUsers()
        .then(d => { this.activeUsers = (d || []).map(u => ({ id: u.id, name: [u.firstName, u.lastName].filter(Boolean).join(' ') })) })
        .catch(() => { this.activeUsers = [] })
      this.loadSteps()
      this.quotationService.getAllActiveQuotationStatuses()
        .then(data => { this.quoteStatuses = data || [] })
        .catch(() => { this.quoteStatuses = [] })
      return this.followupService.getSettings()
        .then(data => {
          this.saved = data
          this.form = toForm(data)
          this.loadError = ''
        })
        .catch(err => {
          this.loadError = errorMessage(err, 'The follow-up settings could not be loaded.')
          this.$toast.add({ severity: 'error', summary: 'Not loaded', detail: this.loadError, life: 5000 })
        })
        .finally(() => { this.loading = false })
    },
    askRunNow() {
      this.runNowDialog = true
    },
    runNow() {
      this.runNowDialog = false
      this.running = true
      return this.followupService.runNow()
        .then(result => {
          this.$toast.add({ severity: 'success', summary: 'Follow-up pass finished', detail: runNowSummary(result), life: 8000 })
        })
        .catch(err => {
          // 409: a pass is already running, or the user may not do this
          this.$toast.add({
            severity: 'error',
            summary: 'Not run',
            detail: errorMessage(err, 'The follow-up pass could not be run.'),
            life: 5000
          })
        })
        .finally(() => { this.running = false })
    },
    reset() {
      this.errors = {}
      this.form = toForm(this.saved)
    },
    save() {
      this.errors = {}
      this.saving = true
      return this.followupService.saveSettings(toPayload(this.form))
        .then(data => {
          this.saved = data
          this.form = toForm(data)
          this.$toast.add({
            severity: 'success',
            summary: 'Follow-up settings saved',
            detail: 'The follow-up settings were saved.',
            life: 5000
          })
        })
        .catch(err => {
          // a 400 may carry field messages (shown inline) and a top-level error (e.g. unknown keys);
          // a 409 carries only the top-level error. The toast always shows the top-level one when present.
          this.errors = fieldErrors(err)
          var withError = tabWithError(this.errors)
          if (withError) this.showTab(withError)
          this.$toast.add({
            severity: 'error',
            summary: 'Not saved',
            detail: errorMessage(err, 'The follow-up settings could not be saved.'),
            life: 5000
          })
        })
        .finally(() => { this.saving = false })
    }
  }
}
</script>

<style scoped>
.followup-settings { max-width: 760px; }
.followup-settings h5 { margin-top: 1.5rem; }
.followup-settings .followup-summary { padding: 0.5rem 0.75rem; background: #f1f5f9; border-radius: 4px; font-weight: 600; }
.followup-settings .tab-explain { margin-top: 0; }
.followup-settings .hint { display: block; color: #6c757d; margin-bottom: 0.5rem; }
.followup-settings small.p-invalid { display: block; color: #d32f2f; }
.server-warning { background: #fff3cd; color: #664d03; padding: 0.5rem 0.75rem; border-radius: 4px; }
</style>
